import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Button, Spin, message } from 'antd';
import { FileExcelOutlined, DownloadOutlined, PrinterOutlined, LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';
import * as XLSX from 'xlsx';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const CentralHubChart = ({ delivered_to_centralhub_list, datarequested }) => {
  const chartRef = useRef();

  // Excel export functionality
  const exportToExcel = () => {
    try {
      if (!delivered_to_centralhub_list || delivered_to_centralhub_list.length === 0) {
        message.warning('No data available to export');
        return;
      }

      // Get all unique sample types
      const sampleTypes = new Set();
      delivered_to_centralhub_list.forEach(monthData => {
        if (monthData.samples) {
          monthData.samples.forEach(sample => {
            sampleTypes.add(sample.sample_name);
          });
        }
      });

      const sortedSampleTypes = Array.from(sampleTypes).sort();

      // Prepare data for Excel
      const excelData = [];
      
      // Add header row
      const headerRow = ['Month'];
      sortedSampleTypes.forEach(sampleType => {
        headerRow.push(sampleType.toUpperCase());
      });
      headerRow.push('Monthly Total');
      excelData.push(headerRow);

      // Add data rows
      delivered_to_centralhub_list.forEach(monthData => {
        const row = [monthData.month];
        
        sortedSampleTypes.forEach(sampleType => {
          const sampleData = monthData.samples && monthData.samples.find(s => s.sample_name === sampleType);
          const value = sampleData ? sampleData.total_in_month : 0;
          row.push(value);
        });
        
        row.push(monthData.monthly_total || 0);
        excelData.push(row);
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Central Hub Samples');

      // Generate filename with current date
      const filename = `Central_Hub_Samples_Report_${moment().format('DD_MM_YYYY')}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);
      
      message.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data. Please try again.');
    }
  };

  // Image download functionality
  const downloadAsImage = () => {
    try {
      if (chartRef.current) {
        let canvas = null;
        
        // Try multiple ways to access the canvas (Chart.js v2 compatibility)
        if (chartRef.current.chartInstance && chartRef.current.chartInstance.canvas) {
          canvas = chartRef.current.chartInstance.canvas;
        } else if (chartRef.current.chart && chartRef.current.chart.canvas) {
          canvas = chartRef.current.chart.canvas;
        } else if (chartRef.current.canvas) {
          canvas = chartRef.current.canvas;
        } else {
          // Try to find canvas element directly
          const chartContainer = chartRef.current;
          if (chartContainer && chartContainer.querySelector) {
            canvas = chartContainer.querySelector('canvas');
          }
        }
        
        if (canvas) {
          // Use canvas toDataURL method directly
          const url = canvas.toDataURL('image/png', 1.0);
          
          // Create download link
          const link = document.createElement('a');
          link.download = `Central_Hub_Samples_Chart_${moment().format('DD_MM_YYYY')}.png`;
          link.href = url;
          
          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          message.success('Chart image downloaded successfully!');
        } else {
          throw new Error('Chart canvas not accessible');
        }
      } else {
        throw new Error('Chart reference not available');
      }
    } catch (error) {
      console.error('Image download error:', error);
      console.log('Chart ref current:', chartRef.current);
      message.error('Failed to download chart image. Please try again.');
    }
  };

  // Print functionality
  const printChart = () => {
    try {
      if (chartRef.current) {
        let canvas = null;
        
        // Try multiple ways to access the canvas (Chart.js v2 compatibility)
        if (chartRef.current.chartInstance && chartRef.current.chartInstance.canvas) {
          canvas = chartRef.current.chartInstance.canvas;
        } else if (chartRef.current.chart && chartRef.current.chart.canvas) {
          canvas = chartRef.current.chart.canvas;
        } else if (chartRef.current.canvas) {
          canvas = chartRef.current.canvas;
        } else {
          // Try to find canvas element directly
          const chartContainer = chartRef.current;
          if (chartContainer && chartContainer.querySelector) {
            canvas = chartContainer.querySelector('canvas');
          }
        }
        
        if (!canvas) {
          throw new Error('Chart canvas not accessible for printing');
        }
        
        const chartImageUrl = canvas.toDataURL('image/png', 1.0);
        
        // Prepare Excel data for print table
        const sampleTypes = new Set();
        delivered_to_centralhub_list.forEach(monthData => {
          if (monthData.samples) {
            monthData.samples.forEach(sample => {
              sampleTypes.add(sample.sample_name);
            });
          }
        });
        
        const sortedSampleTypes = Array.from(sampleTypes).sort();
        
        // Create table rows for print
        const printTableRows = delivered_to_centralhub_list.map((monthData, index) => {
          const cells = [`<td style="text-align: center; font-weight: bold;">${monthData.month}</td>`];
          
          sortedSampleTypes.forEach(sampleType => {
            const sampleData = monthData.samples && monthData.samples.find(s => s.sample_name === sampleType);
            const value = sampleData ? sampleData.total_in_month : 0;
            cells.push(`<td style="text-align: center;">${value}</td>`);
          });
          
          cells.push(`<td style="text-align: center; font-weight: bold;">${monthData.monthly_total || 0}</td>`);
          
          return `<tr>${cells.join('')}</tr>`;
        }).join('');

        // Create table headers
        const headerCells = ['<th>Month</th>'];
        sortedSampleTypes.forEach(sampleType => {
          headerCells.push(`<th>${sampleType.toUpperCase()}</th>`);
        });
        headerCells.push('<th>Monthly Total</th>');

        const printContent = `
          <html>
            <head>
              <title>Central Hub Samples Report - ${moment().format('DD/MM/YYYY')}</title>
              <style>
                @page {
                  margin: 0.5in;
                  size: landscape;
                }
                
                body { 
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                  margin: 0;
                  padding: 20px;
                  background: white;
                  color: #333;
                  line-height: 1.4;
                }
                
                .print-container {
                  max-width: 100%;
                  margin: 0 auto;
                }
                
                .print-header { 
                  text-align: center; 
                  margin-bottom: 30px; 
                  border-bottom: 3px solid #0092ff;
                  padding-bottom: 20px;
                }
                
                .print-header h1 { 
                  color: #0092ff; 
                  margin-bottom: 10px;
                  font-size: 24px;
                  font-weight: bold;
                  text-transform: uppercase;
                }
                
                .chart-container {
                  text-align: center;
                  margin: 30px 0;
                }
                
                .chart-image {
                  max-width: 100%;
                  height: auto;
                  border: 1px solid #ddd;
                  border-radius: 8px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                
                .data-table-container {
                  margin: 30px 0;
                }
                
                .data-table-title {
                  font-size: 18px;
                  font-weight: bold;
                  margin-bottom: 15px;
                  color: #0092ff;
                  text-align: center;
                }
                
                table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 0 auto;
                  background: white;
                  font-size: 11px;
                }
                
                th { 
                  background: linear-gradient(135deg, #0092ff 0%, #0066cc 100%);
                  color: white;
                  font-weight: bold;
                  padding: 10px 6px;
                  text-align: center;
                  font-size: 10px;
                  text-transform: uppercase;
                  border: 1px solid #0066cc;
                }
                
                td { 
                  border: 1px solid #ddd; 
                  padding: 8px 6px; 
                  font-size: 10px;
                  vertical-align: middle;
                }
                
                tr:nth-child(even) { 
                  background-color: #f8f9fa; 
                }
                
                .print-footer { 
                  margin-top: 30px; 
                  text-align: center; 
                  font-size: 11px; 
                  color: #666;
                  border-top: 2px solid #0092ff;
                  padding-top: 15px;
                }
                
                .report-stats {
                  display: flex;
                  justify-content: space-around;
                  margin: 20px 0;
                  flex-wrap: wrap;
                }
                
                .stat-item {
                  text-align: center;
                  margin: 10px;
                  min-width: 150px;
                }
                
                .stat-value {
                  font-size: 18px;
                  font-weight: bold;
                  color: #0092ff;
                  display: block;
                }
                
                .stat-label {
                  font-size: 12px;
                  color: #666;
                  text-transform: uppercase;
                }
              </style>
            </head>
            <body>
              <div class="print-container">
                <div class="print-header">
                  <h1>Central Hub Samples Delivery Report</h1>
                  <p style="margin: 5px 0; font-size: 14px;">Generated on ${moment().format('MMMM DD, YYYY [at] HH:mm')}</p>
                  <div class="report-stats">
                    <div class="stat-item">
                      <span class="stat-value">${delivered_to_centralhub_list.length}</span>
                      <span class="stat-label">Total Months</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">${sortedSampleTypes.length}</span>
                      <span class="stat-label">Sample Types</span>
                    </div>
                    <div class="stat-item">
                      <span class="stat-value">${delivered_to_centralhub_list.reduce((sum, month) => sum + (month.monthly_total || 0), 0)}</span>
                      <span class="stat-label">Total Samples</span>
                    </div>
                  </div>
                </div>
                
                <div class="chart-container">
                  <img src="${chartImageUrl}" alt="Central Hub Samples Chart" class="chart-image">
                </div>
                
                <div class="data-table-container">
                  <div class="data-table-title">Central Hub Samples Data</div>
                  <table>
                    <thead>
                      <tr>${headerCells.join('')}</tr>
                    </thead>
                    <tbody>
                      ${printTableRows}
                    </tbody>
                  </table>
                </div>
                
                <div class="print-footer">
                  <p><strong>UMOJA Central Hub Samples Tracking System</strong></p>
                  <p>This report contains confidential medical delivery information</p>
                  <p>Report generated automatically on ${moment().format('MMMM DD, YYYY [at] HH:mm')}</p>
                </div>
              </div>
            </body>
          </html>
        `;

        // Open print window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Wait for images to load before printing
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        
        message.success('Print preview opened successfully!');
      }
    } catch (error) {
      console.error('Print error:', error);
      message.error('Failed to print chart. Please try again.');
    }
  };

  // Generate dynamic colors for unlimited sample types
  const generateDynamicColor = (index, alpha = 0.8) => {
    // Predefined professional colors for first 20 sample types
    const predefinedColors = [
      `rgba(54, 162, 235, ${alpha})`,   // Blue
      `rgba(255, 99, 132, ${alpha})`,   // Red  
      `rgba(75, 192, 192, ${alpha})`,   // Teal
      `rgba(255, 206, 86, ${alpha})`,   // Yellow
      `rgba(153, 102, 255, ${alpha})`,  // Purple
      `rgba(255, 159, 64, ${alpha})`,   // Orange
      `rgba(199, 199, 199, ${alpha})`,  // Gray
      `rgba(83, 102, 255, ${alpha})`,   // Indigo
      `rgba(255, 99, 255, ${alpha})`,   // Magenta
      `rgba(99, 255, 132, ${alpha})`,   // Light Green
      `rgba(255, 192, 203, ${alpha})`,  // Pink
      `rgba(64, 224, 208, ${alpha})`,   // Turquoise
      `rgba(255, 218, 185, ${alpha})`,  // Peach
      `rgba(221, 160, 221, ${alpha})`,  // Plum
      `rgba(176, 196, 222, ${alpha})`,  // Light Steel Blue
      `rgba(255, 228, 181, ${alpha})`,  // Moccasin
      `rgba(152, 251, 152, ${alpha})`,  // Pale Green
      `rgba(255, 182, 193, ${alpha})`,  // Light Pink
      `rgba(173, 216, 230, ${alpha})`,  // Light Blue
      `rgba(240, 230, 140, ${alpha})`   // Khaki
    ];

    if (index < predefinedColors.length) {
      return predefinedColors[index];
    }

    // For additional colors beyond predefined, generate using HSL with golden angle
    const goldenAngle = 137.508; // Golden angle in degrees for optimal distribution
    const hue = (index * goldenAngle) % 360;
    const saturation = 65 + (index % 3) * 10; // Vary saturation 65-85%
    const lightness = 50 + (index % 4) * 5;   // Vary lightness 50-65%

    // HSL to RGB conversion
    const hslToRgb = (h, s, l) => {
      s /= 100;
      l /= 100;
      const k = n => (n + h / 30) % 12;
      const a = s * Math.min(l, 1 - l);
      const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
      return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
    };

    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Get colors for sample types
  const getSampleTypeColors = (sampleTypes) => {
    const colors = {};
    sampleTypes.forEach((sampleType, index) => {
      colors[sampleType] = generateDynamicColor(index);
    });
    return colors;
  };

  const prepareChartData = () => {
    if (!delivered_to_centralhub_list || delivered_to_centralhub_list.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Get all unique sample types
    const sampleTypes = new Set();
    delivered_to_centralhub_list.forEach(monthData => {
      if (monthData.samples) {
        monthData.samples.forEach(sample => {
          sampleTypes.add(sample.sample_name);
        });
      }
    });

    const sortedSampleTypes = Array.from(sampleTypes).sort();
    const sampleTypeColors = getSampleTypeColors(sortedSampleTypes);

    // Get month labels
    const labels = delivered_to_centralhub_list.map(item => item.month);

    // Create datasets for each sample type
    const datasets = sortedSampleTypes.map(sampleType => {
      const data = delivered_to_centralhub_list.map(monthData => {
        if (monthData.samples) {
          const sampleData = monthData.samples.find(s => s.sample_name === sampleType);
          return sampleData ? sampleData.total_in_month : 0;
        }
        return 0;
      });

      return {
        label: sampleType.toUpperCase(),
        data: data,
        backgroundColor: sampleTypeColors[sampleType],
        borderColor: sampleTypeColors[sampleType].replace('0.8)', '1)'),
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      };
    });

    return { labels, datasets };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'VOLUME OF SAMPLES DELIVERED TO CENTRAL HUB',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return `Month: ${context[0].label}`;
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y} samples`;
          },
          footer: function(tooltipItems) {
            let total = 0;
            tooltipItems.forEach(function(tooltipItem) {
              total += tooltipItem.parsed.y;
            });
            return `Total: ${total} samples`;
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    scales: {
      xAxes: [{
        display: true,
        scaleLabel: {
          display: true,
          labelString: 'MONTH',
          fontSize: 16,
          fontStyle: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontColor: '#333'
        },
        gridLines: {
          display: false
        },
        ticks: {
          fontSize: 12,
          fontStyle: '500',
          fontColor: '#666'
        }
      }],
      yAxes: [{
        display: true,
        scaleLabel: {
          display: true,
          labelString: 'TOTAL NUMBER OF SAMPLES',
          fontSize: 16,
          fontStyle: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontColor: '#333'
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        ticks: {
          beginAtZero: true,
          stepSize: 1,
          fontSize: 12,
          fontStyle: '500',
          fontColor: '#666',
          callback: function(value, index, values) {
            return Number.isInteger(value) ? value : '';
          }
        }
      }]
    },
    elements: {
      bar: {
        borderWidth: 1,
      }
    }
  };

  if (datarequested === true) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" indicator={antIcon}>
          <div className="content">
            <h3>Loading Central Hub Samples Data...</h3>
          </div>
        </Spin>
      </div>
    );
  }

  const chartData = prepareChartData();

  if (chartData.labels.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div className="content">
          <h3>No central hub samples data available</h3>
          <p>Please adjust your filters and try again</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animated fadeIn" style={{ padding: '20px' }}>
      
      {/* Action Buttons */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        
        {/* Excel Download Button */}
        <Button 
          type="primary" 
          icon={<FileExcelOutlined />}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          onClick={exportToExcel}
        >
          Download Excel
        </Button>

        {/* Image Download Button */}
        <Button 
          type="primary" 
          icon={<DownloadOutlined />}
          style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
          onClick={downloadAsImage}
        >
          Download Image
        </Button>

        {/* Print Button */}
        <Button 
          type="primary" 
          icon={<PrinterOutlined />}
          style={{ backgroundColor: '#fa541c', borderColor: '#fa541c' }}
          onClick={printChart}
        >
          Print Chart
        </Button>

      </div>

      {/* Chart Container */}
      <div style={{ height: '500px' }}>
        <Bar 
          ref={chartRef} 
          data={chartData} 
          options={chartOptions}
          onElementsClick={(elements) => {
            // This helps ensure the chart is properly rendered
          }}
        />
      </div>
      
    </div>
  );
};

export default CentralHubChart; 